import { useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import reservations, {
  ReservationsSearchResponse,
} from 'API/handlers/reservations';
import utils from 'utils';
import uniqBy from 'lodash/uniqBy';

dayjs.extend(utc);

export interface ReservationView {
  reservationId: string;
  title: string;
  reservationCode: string;
  linkProp: {
    pathname: string;
  };
  checkInDateFormatted: string;
  checkOutDateFormatted: string;
  roomAlias: string;
  isDeleted: boolean;
}

function mapReservations({
  reservationList = [],
}: ReservationsSearchResponse): ReservationView[] {
  const formatDateTemplate = utils.getFormattedDate();

  return reservationList.map((reservation) => {
    const URL = `/more-menu/recent-reservations/detail/${
      reservation.reservationId
    }/${reservation.guestName}/${reservation.guestSurname || ''}/`;
    const { checkInDate, checkOutDate } = reservation;
    const isReservationCodeMatch =
      reservation.sourceId === 4 || reservation.sourceId === -4;

    const CODE =
      isReservationCodeMatch && reservation.homeAwayReferenceNumber
        ? reservation.homeAwayReferenceNumber
        : reservation.reservationCode.split('-')[0];

    return {
      reservationId: reservation.reservationId,
      title: `${reservation.guestName} ${reservation.guestSurname}`,
      reservationCode: `#${CODE}`,
      linkProp: {
        pathname: URL,
      },
      checkInDateFormatted: dayjs(checkInDate).utc().format(formatDateTemplate),
      checkOutDateFormatted: dayjs(checkOutDate)
        .utc()
        .format(formatDateTemplate),
      roomAlias: reservation.roomAlias,
      isDeleted: Boolean(reservation.isDeleted),
    };
  });
}

const initialState = {
  items: [] as ReservationView[],
  nextPage: 0,
};

export default function useReservationsAPI() {
  const [data, setData] = useState(initialState);
  const [isLoading, toggleLoading] = useState(false);
  const [isNotFound, toggleNotFoundStatus] = useState(false);

  return {
    reservationsList: data.items,

    isLoading,
    isNotFound,

    resetPagination() {
      setData(initialState);
    },
    pagination: {
      nextPage: data.nextPage,
    },
    clearReservationsList() {
      setData(initialState);
    },
    async fetchReservations(searchText: string) {
      toggleLoading(true);

      try {
        const response = await reservations.search({
          page: data.nextPage,
          searchText,
        });
        toggleLoading(false);

        if (response.data.reservationList.length === 0) {
          toggleNotFoundStatus(true);
        } else {
          toggleNotFoundStatus(false);
          setData({
            items: mapReservations(response.data),
            nextPage: data.nextPage,
          });
        }
      } catch (error) {
        console.error(error);
        setData(initialState);
        toggleLoading(false);
      }
    },
    async fetchReservationsNext({
      searchText,
      page,
    }: {
      searchText: string;
      page: number;
    }) {
      toggleLoading(true);

      const response = await reservations.search({
        page,
        searchText,
      });

      toggleLoading(false);
      if (response.data.reservationList.length !== 0) {
        setData((state) => ({
          items: uniqBy(
            [...state.items, ...mapReservations(response.data)],
            'reservationId'
          ),
          nextPage: page,
        }));
      } else {
        setData((state) => ({
          items: state.items,
          nextPage: page,
        }));
      }
    },
  };
}
